import { useEffect, useState } from 'react';
import { Link2, RotateCw, Send, UserPlus, X } from 'lucide-react';
import { BUILDING_STAFF_INVITE_ROLES, FACILITIES_CONTACT_ROLE_LABELS, type BuildingStaffInviteRole } from '@evi/shared';
import {
  copyBuildingStaffInviteLink, createBuildingStaffInvite, grantBuildingPropertyManager, fetchBuildingStaffInvites, getDisplayErrorMessage, resendBuildingStaffInvite, revokeBuildingStaffInvite,
  type BuildingStaffInviteView,
} from '@/lib/api';
import { INVITE_LINK_COPY_EN, buildingStaffInviteOpen } from '@/lib/inviteLink';
import { useInviteLinkDialog } from './InviteLinkDialog';
import { checkInviteDraft, inviteStatusLabel } from '@/lib/buildingStaffInvites';
import formStyles from './Form.module.css';
import styles from './FacilitiesContactsCard.module.css';
import own from './BuildingStaffInvitesCard.module.css';

/**
 * "Invite building staff" (Warren, 28 Oct 2026, item 5): email, name and
 * role (Property manager, Concierge, Facilities, Other). EV Initiative emails
 * them a branded invite naming who invited them and this location; the link
 * works 7 days. Pending and accepted invites list here with Resend and Copy
 * invite link (pending or expired; 29 October 2026) and Revoke.
 *
 * Accepting gives them a login that sees this location's chargers, live
 * status and alerts only, and puts them on the facilities contacts, so the
 * contacts card above changes when they accept.
 *
 * Shown to whoever the Api lets invite (Super Admin, the site's operator,
 * the site owner); a 403 hides the card.
 */
interface Props {
  locationId: string;
  /** Called after an invite is revoked (the contacts list may have changed). */
  onChanged?: () => void;
}

const ROLE_OPTIONS = BUILDING_STAFF_INVITE_ROLES.map((value) => ({ value, label: FACILITIES_CONTACT_ROLE_LABELS[value] }));

export function BuildingStaffInvitesCard({ locationId, onChanged }: Props) {
  const [invites, setInvites] = useState<BuildingStaffInviteView[] | null>(null);
  const [hidden, setHidden] = useState(false);
  const [canGrantManager, setCanGrantManager] = useState(false);
  const [managerAccess, setManagerAccess] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<BuildingStaffInviteRole | ''>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const inviteLink = useInviteLinkDialog();

  useEffect(() => {
    let cancelled = false;
    setInvites(null);
    setHidden(false);
    setLoadErr('');
    fetchBuildingStaffInvites(locationId)
      .then((data) => { if (!cancelled) { setInvites(data.invites); setCanGrantManager(data.can_grant_property_manager === true); } })
      .catch(async (err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        // 403: this caller cannot invite here; show nothing.
        if (status === 403) { setHidden(true); return; }
        setLoadErr(await getDisplayErrorMessage(err, 'Could not load the invites.'));
      });
    return () => { cancelled = true; };
  }, [locationId]);

  if (hidden) return null;

  const send = async (): Promise<void> => {
    const check = checkInviteDraft({ email, name, role });
    if (!check.ok) { setError(check.error); return; }
    setBusy('send');
    setError('');
    setNotice('');
    try {
      const out = await createBuildingStaffInvite(locationId, { ...check.body, ...(canGrantManager && role === 'property_manager' && managerAccess ? { property_manager_access: true } : {}) });
      setInvites((prev) => [out.invite, ...(prev ?? [])]);
      setEmail(''); setName(''); setRole(''); setManagerAccess(false);
      setNotice(!out.email_sent
        ? 'Invite saved, but the email did not go out. Use Resend.'
        : out.account === 'existing'
          ? `Invite sent. ${out.invite.name} already has an EV Initiative login; accepting adds this location to it.`
          : out.account === 'staff'
            ? `Invite sent. ${out.invite.name} already sees every site; accepting adds them to the facilities contacts.`
            : `Invite sent to ${out.invite.email}. The link works for 7 days.`);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'The invite could not be sent.'));
    } finally {
      setBusy(null);
    }
  };

  const resend = async (inv: BuildingStaffInviteView): Promise<void> => {
    setBusy(inv.id); setError(''); setNotice('');
    try {
      const out = await resendBuildingStaffInvite(locationId, inv.id);
      setInvites((prev) => (prev ?? []).map((x) => (x.id === inv.id ? out.invite : x)));
      setNotice(out.email_sent ? `Sent again to ${inv.email}. The new link works for 7 days.` : 'The email did not go out. Try again shortly.');
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not resend the invite.'));
    } finally { setBusy(null); }
  };

  const grantManager = async (inv: BuildingStaffInviteView): Promise<void> => {
    if (!window.confirm('Give ' + inv.name + ' full Property Manager access to this location, including all chargers, sessions, reports and bank setup?')) return;
    setBusy(inv.id); setError(''); setNotice('');
    try {
      await grantBuildingPropertyManager(locationId, inv.id);
      const data = await fetchBuildingStaffInvites(locationId);
      setInvites(data.invites);
      setNotice(inv.status === 'accepted' ? 'Property Manager access saved. They can refresh Admin to use it.' : 'This invite now grants full Property Manager access when accepted. The existing link still works.');
      onChanged?.();
    } catch (err) { setError(await getDisplayErrorMessage(err, 'Could not update access.')); }
    finally { setBusy(null); }
  };

  const revoke = async (inv: BuildingStaffInviteView): Promise<void> => {
    const question = inv.status === 'accepted'
      ? `Remove ${inv.name}'s access to this location? They stop seeing it and stop getting its alerts.`
      : `Withdraw the invite to ${inv.email}? The link stops working.`;
    if (!window.confirm(question)) return;
    setBusy(inv.id); setError(''); setNotice('');
    try {
      await revokeBuildingStaffInvite(locationId, inv.id);
      setInvites((prev) => (prev ?? []).filter((x) => x.id !== inv.id));
      setNotice(inv.status === 'accepted' ? `${inv.name} no longer has access to this location.` : 'Invite withdrawn.');
      onChanged?.();
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not revoke the invite.'));
    } finally { setBusy(null); }
  };

  return (
    <section className={styles.card} aria-labelledby="building-staff-title">
      <div className={styles.titleRow}>
        <h2 id="building-staff-title" className={styles.title}><UserPlus size={15} /> Invite building staff</h2>
      </div>
      <p className={styles.hint}>
        By default, building staff get a restricted login that shows this location&apos;s
        chargers, whether each one is online, and its alerts, with the steps for a breaker or a restart. No
        driver details or money. A Super Admin can explicitly grant full Property Manager access below. Every invite is limited to this location. They also join the facilities contacts above.
      </p>

      {error && <div className={styles.alertErr} role="alert">{error}</div>}
      {notice && !error && <div className={styles.alertOk}>{notice}</div>}

      <div className={own.form}>
        <input className={formStyles.input} aria-label="Their email" placeholder="name@example.com" type="email" inputMode="email"
          value={email} maxLength={254} autoComplete="off" onChange={(e) => setEmail(e.target.value)} />
        <input className={formStyles.input} aria-label="Their name" placeholder="Name" value={name} maxLength={100}
          autoComplete="off" onChange={(e) => setName(e.target.value)} />
        <select className={formStyles.input} aria-label="Their role" value={role} onChange={(e) => setRole(e.target.value as BuildingStaffInviteRole | '')}>
          <option value="" disabled>Role</option>
          {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button type="button" className={formStyles.btnPrimary} disabled={busy !== null} onClick={() => { void send(); }}>
          <Send size={13} /> {busy === 'send' ? 'Sending...' : 'Send invite'}
        </button>
      </div>

      {canGrantManager && role === 'property_manager' && <label className={styles.hint}>
        <input type="checkbox" checked={managerAccess} onChange={e => setManagerAccess(e.target.checked)} />
        Full Property Manager access: all chargers, sessions, reports and bank setup for this location.
      </label>}

      {loadErr && <div className={styles.alertErr}>{loadErr}</div>}
      {!invites && !loadErr && <p className={styles.hint}>Loading...</p>}
      {invites && invites.length === 0 && <p className={styles.hint}>No invites yet.</p>}
      {invites && invites.length > 0 && (
        <ul className={styles.list}>
          {invites.map((inv) => (
            <li key={inv.id} className={styles.item}>
              <span className={styles.who}>
                <span className={styles.name}>{inv.name}</span>
                <span className={styles.email}>{inv.email} · {inv.role_label}</span>
                <span className={styles.source}>{inviteStatusLabel(inv)}</span>
                <span className={styles.source}>{inv.property_manager_access ? 'Full Property Manager access' : 'Chargers, status and alerts only'}</span>
              </span>
              <span className={own.actions}>
                {canGrantManager && inv.role === 'property_manager' && !inv.property_manager_access && <button type="button" className={formStyles.btnSecondary} disabled={busy !== null} onClick={() => { void grantManager(inv); }}>Grant Property Manager access</button>}
                {/* Resend and Copy invite link (29 October 2026): one rule for both, and the same gate at the Api. */}
                {buildingStaffInviteOpen(inv) && (
                  <>
                    <button type="button" className={formStyles.btnSecondary} disabled={busy !== null} onClick={() => { void resend(inv); }}>
                      <RotateCw size={13} /> {busy === inv.id ? 'Sending...' : 'Resend'}
                    </button>
                    <button type="button" className={formStyles.btnSecondary} disabled={busy !== null || inviteLink.busyFor !== null}
                      onClick={() => { void inviteLink.open({ email: inv.email, name: inv.name }, () => copyBuildingStaffInviteLink(locationId, inv.id)); }}>
                      <Link2 size={13} /> {INVITE_LINK_COPY_EN.action}
                    </button>
                  </>
                )}
                <button type="button" className={formStyles.btnSecondary} disabled={busy !== null} onClick={() => { void revoke(inv); }}>
                  <X size={13} /> Revoke
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {inviteLink.dialog}
    </section>
  );
}
